import {imageIdentity, VariantImageCache} from './growthlab-image-loader.js';

export function resolveCompactProduct(variants, options, quantity) {
  const variant = variants.find(item => item.options.length === options.length && item.options.every((value, index) => value === options[index]))
    || (options.length === 0 && variants.length === 1 && variants[0].options.length === 1 && variants[0].options[0] === 'Default Title' ? variants[0] : undefined);
  const validQuantity = Number.isSafeInteger(quantity) && quantity >= 1 && Number.isSafeInteger((variant?.price || 0) * quantity);
  const error = !variant?.available ? 'unavailable' : (!validQuantity ? 'quantity_error' : null);
  return {variant, error, total:error ? null : variant.price * quantity, items:error ? [] : [{id:variant.id, quantity}]};
}

export class GrowthlabProductCompact extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    try {
      this.catalog = JSON.parse(this.querySelector('[data-product-data]').textContent);
    } catch {
      return;
    }
    this.form = this.querySelector('form');
    this.inputs = [...this.querySelectorAll('[data-option-index]')];
    this.quantity = this.querySelector('[name="quantity"]');
    this.image = this.querySelector('[data-product-image]');
    this.fallback = this.querySelector('[data-fallback]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.enhanced = this.querySelector('[data-enhanced], .gpc-enhanced, .gz-enhanced');
    if (!Array.isArray(this.catalog.variants) || !this.catalog.variants.length || !this.form || !this.quantity || !this.variantInput || !this.enhanced || !this.fallback) return;
    const byId = id => this.catalog.variants.find(item => String(item.id) === String(id));
    this.defaultVariant = byId(this.dataset.defaultVariantId || this.catalog.defaultVariantId)
      || byId(this.variantInput.value) || this.catalog.variants.find(item => item.available) || this.catalog.variants[0];
    const initial = byId(new URL(location.href).searchParams.get('variant')) || this.defaultVariant;
    this.selectVariant(initial);
    this.initialImage = this.catalog.image || this.image?.getAttribute('src');
    this.imageRequest = (this.imageRequest || 0) + 1;
    this.galleryCache = new VariantImageCache();
    this.listeners = new AbortController();
    const listenerOptions = {signal:this.listeners.signal};
    this.addEventListener('change', event => {
      if (event.target.matches('[data-option-index]')) this.update(true);
      if (event.target === this.quantity) this.update();
    }, listenerOptions);
    this.quantity.addEventListener('input', () => this.update(), listenerOptions);
    const warmChoice = event => {
      const input = event.target.closest('label')?.querySelector('[data-option-index]');
      if (!input || input.checked || input.disabled || !this.state?.variant) return;
      const options = [...this.state.variant.options];
      options[Number(input.dataset.optionIndex)] = input.value;
      const variant = this.catalog.variants.find(item => item.available && item.options.every((value, index) => value === options[index]));
      if (variant) this.galleryCache.warm(variant.image);
    };
    this.addEventListener('pointerover', warmChoice, listenerOptions);
    this.addEventListener('focusin', warmChoice, listenerOptions);
    this.addEventListener('click', event => {
      const step = event.target.closest('[data-quantity-step]');
      if (step && !this.busy) {
        const current = Number(this.quantity.value);
        const next = (Number.isSafeInteger(current) ? current : 1) + Number(step.dataset.quantityStep);
        this.quantity.value = String(Math.max(1, Math.min(Number.MAX_SAFE_INTEGER, next)));
        this.update();
      }
      const photo = event.target.closest('[data-photo]');
      if (photo) this.showImage(photo.dataset.photo, photo.getAttribute('aria-label'));
    }, listenerOptions);
    this.form.addEventListener('submit', event => this.submit(event), listenerOptions);
    window.addEventListener('popstate', () => {
      const variant = byId(new URL(location.href).searchParams.get('variant')) || this.defaultVariant;
      this.selectVariant(variant);
      this.update(true, false);
    }, listenerOptions);
    this.ready = true;
    this.fallback.hidden = true;
    this.fallback.querySelector('select').disabled = true;
    this.enhanced.hidden = false;
    this.update(true, false);
  }
  disconnectedCallback() {
    this.listeners?.abort();
    this.galleryCache?.cancel();
    this.imageRequest++;
    this.image?.parentElement.removeAttribute('aria-busy');
    this.ready = false;
  }
  selectVariant(variant) {
    this.inputs.forEach(input => input.checked = variant.options[Number(input.dataset.optionIndex)] === input.value);
    this.singletonOptions = this.inputs.length ? null : [...variant.options];
  }
  update(changeImage = false, updateURL = true) {
    const options = this.singletonOptions ? [...this.singletonOptions] : [];
    this.inputs.filter(input => input.checked).forEach(input => options[Number(input.dataset.optionIndex)] = input.value);
    this.state = resolveCompactProduct(this.catalog.variants, options, Number(this.quantity.value));
    const {variant, error, total} = this.state;
    const imageNote = this.querySelector('[data-image-note]');
    if (imageNote) {
      imageNote.textContent = variant?.imageNote || '';
      imageNote.hidden = !imageNote.textContent;
    }
    this.querySelector('[data-total]').textContent = variant
      ? new Intl.NumberFormat(this.dataset.locale, {style:'currency', currency:this.dataset.currency, currencyDisplay:'narrowSymbol'}).format((total ?? variant.price) / 100)
      : this.catalog.labels.unavailable;
    this.querySelector('[data-submit]').disabled = !!error || !!this.busy;
    this.querySelector('[data-submit]').textContent = this.busy ? this.catalog.labels.adding : this.catalog.labels.add;
    this.variantInput.value = variant?.id || '';
    this.variantInput.disabled = !!error;
    this.querySelector('[data-cart-error]').textContent = error ? this.catalog.labels[error] : (this.cartError || '');
    this.inputs.forEach(input => input.disabled = !!this.busy);
    this.querySelectorAll('[data-quantity-step]').forEach(button => button.disabled = !!this.busy);
    this.quantity.disabled = !!this.busy;
    if (updateURL) {
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState(history.state, '', url);
    }
    if (changeImage) {
      if (variant) this.showImage(variant.image || this.initialImage, this.catalog.labels.photo);
      else {
        this.galleryCache.cancel();
        this.imageRequest++;
        this.image?.parentElement.removeAttribute('aria-busy');
      }
    }
  }
  async showImage(src, alt) {
    if (!this.image || !src) return;
    this.galleryCache.cancel();
    const request = ++this.imageRequest;
    const selectedImage = imageIdentity(src);
    const markThumbnail = () => this.querySelectorAll('[data-photo]').forEach(button => button.setAttribute('aria-pressed', String(imageIdentity(button.dataset.photo) === selectedImage)));
    const preload = () => {
      const variant = this.state?.variant;
      this.galleryCache.schedule(this.catalog.variants, variant, () => this.isConnected && request === this.imageRequest && this.state?.variant?.id === variant?.id);
    };
    if (selectedImage === imageIdentity(this.image.currentSrc || this.image.getAttribute('src'))) {
      this.image.parentElement.removeAttribute('aria-busy');
      markThumbnail();
      if (this.image.complete && this.image.naturalWidth) preload();
      else this.image.addEventListener('load', () => { if (request === this.imageRequest && this.isConnected) preload(); }, {once:true, signal:this.listeners.signal});
      return;
    }
    this.image.parentElement.setAttribute('aria-busy', 'true');
    try {
      const loaded = await this.galleryCache.load(src);
      if (request !== this.imageRequest || !this.isConnected) return;
      this.image.src = loaded;
      this.image.removeAttribute('srcset');
      this.image.alt = alt || this.catalog.labels.photo;
      markThumbnail();
      preload();
    } catch {} finally {
      if (request === this.imageRequest) this.image.parentElement.removeAttribute('aria-busy');
    }
  }
  loadCart() {
    return import(this.dataset.cartModule);
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    this.cartError = '';
    this.update();
    if (this.state.error || !this.form.reportValidity()) return;
    const items = this.state.items.map(item => ({...item}));
    this.busy = true;
    this.querySelector('[data-cart-fallback]').hidden = true;
    this.update();
    try {
      const {addToDrawer} = await this.loadCart();
      await addToDrawer(items, this.dataset.addUrl, this.dataset.cartUrl);
    } catch (error) {
      this.cartError = error?.confirmed ? this.catalog.labels.cart_added : (error?.rejected ? error.message : this.catalog.labels.cart_uncertain);
      this.querySelector('[data-cart-fallback]').hidden = false;
    } finally {
      this.busy = false;
      if (this.ready && this.isConnected) this.update();
    }
  }
}

if (!customElements.get('growthlab-product-compact')) customElements.define('growthlab-product-compact', GrowthlabProductCompact);
